import { Suspense } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { SessionProvider } from '@/core/contexts/SessionContext';
import { PublicConfigProvider } from '@/core/contexts/PublicConfigContext';
import { FloatingChatProvider } from '@/core/contexts/FloatingChatContext';
import { CartProvider } from '@/features/storefront/contexts/CartContext';
import { NewRelicInitializer } from '@/core/components/NewRelicInitializer';
import { OneSignalInitializer } from '@/core/components/OneSignalInitializer';
import { AnonymousIdentityInitializer } from '@/core/components/AnonymousIdentityInitializer';
import { ThemeSync } from '@/core/components/ThemeSync';
import { ToastProvider } from '@/hooks/useToast';
import ErrorBoundary from './pages/ErrorBoundary';
import LoadingPage from './pages/LoadingPage';
import { CookieBanner } from '@/core/ui/CookieBanner';
import { FloatingBubbles } from '@/components/ui/FloatingBubbles';
import { MetaTagsInjector } from '@/features/admin/MetaTags/components/MetaTagsInjector';
import { QUERY_CONFIG } from '@/core/config';

// Create a client with sensible defaults using QUERY_CONFIG.STANDARD
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...QUERY_CONFIG.STANDARD,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <PublicConfigProvider>
          <NewRelicInitializer />
          <MetaTagsInjector />
          <ToastProvider>
            {/* SessionProvider unifies auth + preferences in a single context */}
            <SessionProvider>
              <CartProvider>
                <FloatingChatProvider>
                  <ThemeSync />
                  <AnonymousIdentityInitializer />
                  <OneSignalInitializer />
                  <Suspense fallback={<LoadingPage />}>
                    <RouterProvider router={router} />
                  </Suspense>
                  <CookieBanner />
                  {/* Global floating bubbles (WhatsApp + Sales Chat) */}
                  <FloatingBubbles
                    whatsappNumber="" // TODO: Configure WhatsApp number
                    botName="Ventas"
                    chatPrimaryColor="#4f46e5"
                  />
                </FloatingChatProvider>
              </CartProvider>
            </SessionProvider>
          </ToastProvider>
        </PublicConfigProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
